import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Typography, Select, TimePicker, Row, Col } from 'antd';
import { BankOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from '../layout/layout';
import { apiClient } from '../../config/apiClient';
import { useModulos } from '../../context/modulosContext';

const { TextArea } = Input;
const { Text } = Typography;

const DatosAcademia = () => {
  const navigate = useNavigate();
  const { modulosActivos, modulosLoaded } = useModulos();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoBase64, setFotoBase64] = useState(null);
  const [fotoTipo, setFotoTipo] = useState(null);
  const [fotoRemoved, setFotoRemoved] = useState(false);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingLocalidades, setLoadingLocalidades] = useState(false);

  const loadPaises = async () => {
    try {
      const response = await apiClient.get('/paises');
      if (response.ok) {
        const data = await response.json();
        setPaises(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error al cargar países:', e);
    }
  };

  const loadProvincias = async (paisId) => {
    if (!paisId) {
      setProvincias([]);
      return;
    }
    setLoadingProvincias(true);
    try {
      const response = await apiClient.get(`/provincias/pais/${paisId}`);
      if (response.ok) {
        const data = await response.json();
        setProvincias(Array.isArray(data) ? data : []);
      } else {
        setProvincias([]);
      }
    } catch (e) {
      console.error('Error al cargar provincias:', e);
      setProvincias([]);
    } finally {
      setLoadingProvincias(false);
    }
  };

  const loadLocalidades = async (provinciaId) => {
    if (!provinciaId) {
      setLocalidades([]);
      return;
    }
    setLoadingLocalidades(true);
    try {
      const response = await apiClient.get(`/localidades/provincia/${provinciaId}`);
      if (response.ok) {
        const data = await response.json();
        setLocalidades(Array.isArray(data) ? data : []);
      } else {
        setLocalidades([]);
      }
    } catch (e) {
      console.error('Error al cargar localidades:', e);
      setLocalidades([]);
    } finally {
      setLoadingLocalidades(false);
    }
  };

  const loadAcademia = async () => {
    setLoading(true);
    try {
      await loadPaises();
      const response = await apiClient.get('/academia-datos');
      if (response.ok) {
        const data = await response.json();
        const paisId = data.aca_pais_id != null ? Number(data.aca_pais_id) : undefined;
        const provinciaId = data.aca_provincia_id != null ? Number(data.aca_provincia_id) : undefined;
        const localidadId = data.aca_localidad != null ? Number(data.aca_localidad) : undefined;

        if (paisId) await loadProvincias(paisId);
        if (provinciaId) await loadLocalidades(provinciaId);

        form.setFieldsValue({
          aca_nombre: data.aca_nombre ?? '',
          aca_direccion: data.aca_direccion ?? '',
          aca_latitud: data.aca_latitud != null ? String(data.aca_latitud) : '',
          aca_longitud: data.aca_longitud != null ? String(data.aca_longitud) : '',
          aca_telefono: data.aca_telefono ?? '',
          aca_instagram: data.aca_instagram ?? '',
          aca_horario_desde: data.aca_horario_desde ? dayjs(data.aca_horario_desde, 'HH:mm') : null,
          aca_horario_hasta: data.aca_horario_hasta ? dayjs(data.aca_horario_hasta, 'HH:mm') : null,
          aca_pais_id: paisId,
          aca_provincia_id: provinciaId,
          aca_localidad: localidadId,
          aca_descripcion: data.aca_descripcion ?? '',
        });
        if (data.aca_foto_base64) {
          setFotoBase64(data.aca_foto_base64);
          setFotoTipo(data.aca_foto_tipo || 'image/jpeg');
          setFotoPreview(`data:${data.aca_foto_tipo || 'image/jpeg'};base64,${data.aca_foto_base64}`);
        } else {
          setFotoBase64(null);
          setFotoTipo(null);
          setFotoPreview(null);
          setFotoRemoved(false);
        }
      }
    } catch (error) {
      console.error('Error al cargar datos de academia:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!modulosLoaded) return;
    if (!modulosActivos.academia) {
      message.warning('El módulo academia está deshabilitado.');
      navigate('/home-administrador', { replace: true });
      return;
    }
    loadAcademia();
  }, [modulosLoaded, modulosActivos.academia]);

  if (!modulosLoaded) {
    return (
      <MainLayout>
        <Spin spinning tip="Cargando..." />
      </MainLayout>
    );
  }

  const handlePaisChange = (paisId) => {
    form.setFieldsValue({ aca_provincia_id: undefined, aca_localidad: undefined });
    setProvincias([]);
    setLocalidades([]);
    loadProvincias(paisId);
  };

  const handleProvinciaChange = (provinciaId) => {
    form.setFieldsValue({ aca_localidad: undefined });
    setLocalidades([]);
    loadLocalidades(provinciaId);
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.warning('Elegí una imagen (JPG, PNG, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.startsWith('data:')) {
        const base64 = result.split(',')[1];
        setFotoBase64(base64);
        setFotoTipo(file.type);
        setFotoPreview(result);
        setFotoRemoved(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const quitarFoto = () => {
    setFotoBase64(null);
    setFotoTipo(null);
    setFotoPreview(null);
    setFotoRemoved(true);
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const body = {
        aca_nombre: values.aca_nombre,
        aca_direccion: values.aca_direccion,
        aca_latitud: values.aca_latitud?.trim() ? values.aca_latitud.trim() : null,
        aca_longitud: values.aca_longitud?.trim() ? values.aca_longitud.trim() : null,
        aca_telefono: values.aca_telefono,
        aca_instagram: values.aca_instagram,
        aca_horario_desde: values.aca_horario_desde ? values.aca_horario_desde.format('HH:mm') : null,
        aca_horario_hasta: values.aca_horario_hasta ? values.aca_horario_hasta.format('HH:mm') : null,
        aca_localidad: values.aca_localidad ?? null,
        aca_descripcion: values.aca_descripcion,
        aca_foto_base64: fotoBase64 || undefined,
        aca_foto_tipo: fotoTipo || undefined,
        clearFoto: fotoRemoved,
      };
      const response = await apiClient.put('/academia-datos', body);
      if (response.ok) {
        message.success('Datos de academia guardados correctamente');
        loadAcademia();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      message.error('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <Card
        title={(
          <span>
            <BankOutlined style={{ marginRight: 8 }} />
            Datos de academia
          </span>
        )}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_nombre" label="Nombre" rules={[{ required: false }]}>
                  <Input placeholder="Nombre de la academia" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_direccion" label="Dirección" rules={[{ required: false }]}>
                  <Input placeholder="Dirección" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_telefono" label="Teléfono" rules={[{ required: false }]}>
                  <Input placeholder="Teléfono" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item name="aca_latitud" label="Latitud (opcional)" rules={[{ required: false }]}>
                  <Input placeholder="Ej: -32.4085" type="number" step="any" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="aca_longitud" label="Longitud (opcional)" rules={[{ required: false }]}>
                  <Input placeholder="Ej: -63.2596" type="number" step="any" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_pais_id" label="País" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar país"
                    allowClear
                    onChange={handlePaisChange}
                    options={paises.map((p) => ({ value: p.pai_id, label: p.pai_nombre }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_provincia_id" label="Provincia" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar provincia"
                    allowClear
                    loading={loadingProvincias}
                    onChange={handleProvinciaChange}
                    options={provincias.map((p) => ({ value: p.pro_id, label: p.pro_nombre }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_localidad" label="Localidad" rules={[{ required: false }]}>
                  <Select
                    placeholder="Seleccionar localidad"
                    allowClear
                    loading={loadingLocalidades}
                    options={localidades.map((l) => ({ value: l.loc_id, label: l.loc_nombre }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_instagram" label="Instagram" rules={[{ required: false }]}>
                  <Input placeholder="Usuario o enlace de Instagram" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_horario_desde" label="Horario (desde)" rules={[{ required: false }]}>
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="aca_horario_hasta" label="Horario (hasta)" rules={[{ required: false }]}>
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="aca_descripcion" label="Descripción" rules={[{ required: false }]}>
              <TextArea rows={4} placeholder="Descripción de la academia" />
            </Form.Item>

            <Form.Item label="Foto">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {fotoPreview ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <img
                      src={fotoPreview}
                      alt="Vista previa"
                      style={{
                        maxWidth: 200,
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #404040',
                      }}
                    />
                    <div>
                      <Button type="default" danger icon={<DeleteOutlined />} onClick={quitarFoto} size="small">
                        Quitar foto
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="foto-academia-input"
                  />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => document.getElementById('foto-academia-input').click()}
                  >
                    {fotoPreview ? 'Cambiar foto' : 'Subir foto'}
                  </Button>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    Se guarda en la base de datos (BLOB)
                  </Text>
                </div>
              </div>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Guardar
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
};

export default DatosAcademia;
